import { format } from 'date-fns';

/**
 * Normalizes a date for use in cache keys
 * Removes time component to prevent multiple cache entries for the same date
 */
export function normalizeDateForCache(date: Date | string | number): string {
  const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  return format(dateObj, 'yyyy-MM-dd');
}

/**
 * Creates a normalized cache key for experience data
 */
export function createExperienceCacheKey(experienceId: string, date?: Date | string | number): string {
  const normalizedId = experienceId.replaceAll('%22', '');

  if (date) {
    const normalizedDate = normalizeDateForCache(date);
    return `experience_${normalizedId}_${normalizedDate}`;
  }

  return `experience_${normalizedId}`;
}

/**
 * Creates a normalized cache key for seats data
 */
export function createSeatsCacheKey(experienceId: string, configurationId: string, startsAt: number | Date): string {
  const normalizedId = experienceId.replaceAll('%22', '');
  const timestamp = typeof startsAt === 'number' ? startsAt : startsAt.getTime();
  return `seats_${normalizedId}_${configurationId}_${timestamp}`;
}

/**
 * Manages localStorage size by removing oldest entries when approaching limit
 */
export function manageCacheSize(prefix: string, maxSizeKB: number = 2048): void {
  try {
    const keys = Object.keys(localStorage);
    const cacheEntries: Array<{ key: string; timestamp: number; size: number }> = [];

    keys.forEach((key) => {
      if (key.startsWith(prefix)) {
        try {
          const value = localStorage.getItem(key);
          if (value) {
            const parsed = JSON.parse(value);
            cacheEntries.push({
              key,
              timestamp: parsed.timestamp || 0,
              size: key.length + value.length,
            });
          }
        } catch (error) {
          localStorage.removeItem(key);
        }
      }
    });

    const totalSize = cacheEntries.reduce((sum, entry) => sum + entry.size, 0) / 1024;

    if (totalSize > maxSizeKB) {
      cacheEntries.sort((a, b) => a.timestamp - b.timestamp);

      let currentSize = totalSize;
      for (const entry of cacheEntries) {
        if (currentSize <= maxSizeKB * 0.8) break;

        localStorage.removeItem(entry.key);
        currentSize -= entry.size / 1024;
      }
    }
  } catch (error) {
    console.warn('Error managing cache size:', error);
  }
}
